import { useApolloClient } from "@apollo/client";
import { MenuItemWithIcon } from "@msys/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteQuoteTemplateMutation } from "../quote/TemplateQuotes.generated.js";
import { DeleteTemplateMenuItem_QuoteTemplateFragment } from "./DeleteTemplateMenuItem.generated.js";

interface Props {
  doc: DeleteTemplateMenuItem_QuoteTemplateFragment;
  pathToDocList: string;
}

export const DeleteTemplateMenuItem = ({ doc, pathToDocList }: Props) => {
  const client = useApolloClient();
  const navigate = useNavigate();

  const { t } = useTranslate(["Quote", "Global"]);

  const { enqueueSnackbar } = useSnackbar();

  const [deleteQuoteTemplateMutation, { loading: deleteQuoteTemplateLoading }] =
    useDeleteQuoteTemplateMutation({
      client,
    });

  const isPublishedWithOtherOrgs =
    doc.hasAnyPublishedVersion && Boolean(doc.templateSharing);
  const isUsedInOtherTemplates = doc.usedInOtherTemplates;

  const deleteQuoteTemplate = async () => {
    if (isPublishedWithOtherOrgs || isUsedInOtherTemplates) return;
    try {
      await deleteQuoteTemplateMutation({
        variables: {
          input: {
            quoteTemplateId: doc.id,
          },
        },
      });

      navigate(pathToDocList, { replace: true });
      enqueueSnackbar(
        t("Quote template deleted", {
          ns: "Quote",
        })
      );
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  if (isPublishedWithOtherOrgs) {
    return (
      <Tooltip
        title={t("Template is published and can not be deleted", {
          ns: "Quote",
        })}
      >
        <Box display="flex">
          <MenuItemWithIcon icon={<DeleteIcon />} disabled={true}>
            {t("Delete Template", { ns: "Quote" })}
          </MenuItemWithIcon>
        </Box>
      </Tooltip>
    );
  }
  if (isUsedInOtherTemplates) {
    return (
      <Tooltip
        title={t("Template is used in other templates and can not be deleted", {
          ns: "Quote",
        })}
      >
        <Box display="flex">
          <MenuItemWithIcon icon={<DeleteIcon />} disabled={true}>
            {t("Delete Template", { ns: "Quote" })}
          </MenuItemWithIcon>
        </Box>
      </Tooltip>
    );
  }

  return (
    <MenuItemWithIcon
      icon={<DeleteIcon />}
      onClick={deleteQuoteTemplate}
      disabled={deleteQuoteTemplateLoading}
    >
      {t("Delete Template", { ns: "Quote" })}
    </MenuItemWithIcon>
  );
};
